import React, { ChangeEvent, Component } from 'react';

import { Upload } from '@mui/icons-material';
import { Button, MenuItem, Tooltip, Typography } from '@mui/material';

import { isFileSystemAccessSupported } from './RecentFiles';

export interface ReceivedFile {
  fileName: string;
  contents: ArrayBuffer;
  handle?: FileSystemFileHandle;
}

export interface FileReceiverProps {
  handleFiles: (files: ReceivedFile[]) => void;
}

class FileReceiverHiddenInput extends Component<FileReceiverProps> {
  private inputRef = React.createRef<HTMLInputElement>();

  render() {
    return (
      <div style={{ display: 'none' }}>
        <input
          accept="*"
          ref={this.inputRef}
          multiple
          type="file"
          onChange={(e) => this.handleFileChange(e)}
        />
      </div>
    );
  }

  click() {
    this.inputRef.current?.click();
  }

  private async handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    event.persist();
    if (!event.target.files) {
      return;
    }

    const files: File[] = Array.from(event.target.files);
    try {
      const receivedFiles: ReceivedFile[] = await Promise.all(
        files.map(async (file) => {
          const contents = await this.getFileContents(file);
          if (!contents) {
            throw 1;
          }

          return {
            fileName: file.name,
            contents,
          };
        }),
      );

      event.target.value = '';

      this.props.handleFiles(receivedFiles);
    } catch (error) {
      alert(error);
    }
  }

  private getFileContents(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => {
        resolve(reader.result as ArrayBuffer);
      };
      reader.readAsArrayBuffer(file);
    });
  }
}

// Use File System Access API to get file handles
async function openFilesWithHandles(): Promise<ReceivedFile[]> {
  const handles = await window.showOpenFilePicker({ multiple: true });

  const receivedFiles: ReceivedFile[] = await Promise.all(
    handles.map(async (handle) => {
      const file = await handle.getFile();
      const contents = await file.arrayBuffer();

      return {
        fileName: file.name,
        contents,
        handle,
      };
    }),
  );

  return receivedFiles;
}

export interface FileReceiverMenuItemProps extends FileReceiverProps {
  showTitle: boolean;
}

export class FileReceiverMenuItem extends Component<FileReceiverMenuItemProps> {
  private inputRef = React.createRef<FileReceiverHiddenInput>();

  // To avoid recursive click handling.
  private isHandlingClick = false;

  render() {
    const menuItem = (
      <MenuItem
        onClick={() => {
          this.handleClick();
        }}
      >
        <Upload />
        {this.props.showTitle ? <Typography textAlign="center">UPLOAD LOGS</Typography> : null}
        <FileReceiverHiddenInput ref={this.inputRef} handleFiles={this.props.handleFiles} />
      </MenuItem>
    );

    if (this.props.showTitle) {
      return menuItem;
    }
    return <Tooltip title="Upload logs">{menuItem}</Tooltip>;
  }

  private async handleClick() {
    if (this.isHandlingClick) {
      return;
    }
    this.isHandlingClick = true;

    try {
      // Use File System Access API if available (Chrome/Edge)
      if (isFileSystemAccessSupported()) {
        try {
          const files = await openFilesWithHandles();
          this.props.handleFiles(files);
          return;
        } catch (error) {
          // User cancelled or API failed, fall back to input
          if ((error as Error).name === 'AbortError') {
            return;
          }
          console.warn('File System Access API failed, falling back to input', error);
        }
      }

      // Fallback to regular file input
      this.inputRef.current?.click();
    } finally {
      this.isHandlingClick = false;
    }
  }
}

export class FileReceiverButton extends Component<{ title?: string } & FileReceiverProps> {
  private inputRef = React.createRef<FileReceiverHiddenInput>();

  // To avoid recursive click handling.
  private isHandlingClick = false;

  render() {
    return (
      <Button
        variant="contained"
        color="primary"
        onClick={() => {
          this.handleClick();
        }}
      >
        <Upload />
        <Typography textAlign="center">{this.props.title || 'UPLOAD LOGS'}</Typography>
        <FileReceiverHiddenInput ref={this.inputRef} handleFiles={this.props.handleFiles} />
      </Button>
    );
  }

  private async handleClick() {
    if (this.isHandlingClick) {
      return;
    }
    this.isHandlingClick = true;

    try {
      // Use File System Access API if available (Chrome/Edge)
      if (isFileSystemAccessSupported()) {
        try {
          const files = await openFilesWithHandles();
          this.props.handleFiles(files);
          return;
        } catch (error) {
          // User cancelled or API failed, fall back to input
          if ((error as Error).name === 'AbortError') {
            return;
          }
          console.warn('File System Access API failed, falling back to input', error);
        }
      }

      // Fallback to regular file input
      this.inputRef.current?.click();
    } finally {
      this.isHandlingClick = false;
    }
  }
}
